import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ShoppingCart, 
  Package, 
  AlertTriangle, 
  DollarSign,
  TrendingUp,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, salesRes, topRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/recent-sales`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/top-products`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setRecentSales(salesRes.data);
      setTopProducts(topRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">
          Bienvenido, {user?.name}
        </h1>
        <p className="text-stone-500 mt-1 font-body">
          Resumen de tu negocio hoy
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="stat-sales-today">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Ventas Hoy</CardTitle>
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{formatCurrency(stats?.today_sales_total || 0)}</p>
            <p className="text-sm text-stone-500 mt-1">{stats?.today_sales_count || 0} transacciones</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="stat-products">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Productos</CardTitle>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{stats?.total_products || 0}</p>
            <p className="text-sm text-stone-500 mt-1">{stats?.total_categories || 0} categorías</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="stat-low-stock">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Stock Bajo</CardTitle>
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{stats?.low_stock_products || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Productos con stock ≤ 5</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="stat-cash-sessions">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-stone-500">Cajas Abiertas</CardTitle>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{stats?.open_cash_sessions || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Cajeros activos</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sales & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Sales */}
        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm" data-testid="recent-sales-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-bold text-stone-900">
              <Clock className="w-5 h-5 text-amber-600" />
              Ventas Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentSales.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No hay ventas recientes</p>
            ) : (
              <div className="space-y-4">
                {recentSales.slice(0, 5).map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                    <div>
                      <p className="font-medium text-stone-900">Venta #{sale.sale_number}</p>
                      <p className="text-sm text-stone-500">{formatTime(sale.created_at)} - {sale.cashier_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-900">{formatCurrency(sale.total)}</p>
                      <p className="text-xs text-stone-500 capitalize">{sale.payment_method}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="bg-white border-stone-200 rounded-2xl shadow-sm" data-testid="top-products-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-bold text-stone-900">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Productos Más Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <p className="text-stone-500 text-center py-8">Aún no hay datos de ventas</p>
            ) : (
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={product._id} className="flex items-center gap-4 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
                      <span className="text-sm font-bold text-amber-600">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-stone-900">{product.name}</p>
                      <p className="text-sm text-stone-500">{product.total_sold} unidades vendidas</p>
                    </div>
                    <p className="font-semibold text-emerald-600">{formatCurrency(product.total_revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
